import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2, Clock, MapPin, Phone, ArrowRight, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { SiteVisitSlots } from "./quote/SiteVisitSlots";

const longDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";

// Self-service "change my site visit" page — reached from the link in the confirmation / reminder email.
export default function SiteVisitManage() {
  const { token } = useParams();
  const [visit, setVisit] = useState(null);
  const [error, setError] = useState("");
  const [slot, setSlot] = useState({ date: "", time: "" });
  const [busy, setBusy] = useState(false);
  const [moved, setMoved] = useState(null);

  useEffect(() => {
    api.get(`/site-visit/manage/${encodeURIComponent(token)}`).then(r => setVisit(r.data))
      .catch(e => setError(e?.response?.data?.detail || "This link isn't valid — please call us."));
  }, [token]);

  const confirm = async () => {
    if (!slot.date || !slot.time) { toast.error("Pick a new day and time first."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/site-visit/manage/${encodeURIComponent(token)}/reschedule`, slot);
      setMoved(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not move your visit — please try another slot.");
    } finally { setBusy(false); }
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16 max-w-5xl mx-auto" data-testid="site-visit-manage">
      <div className="label-eyebrow mb-3">Your free site visit</div>
      {error && <Invalid message={error} />}
      {!error && !visit && <div className="text-[#6B7280]">Loading your visit…</div>}
      {visit && moved && <Moved v={moved} />}
      {visit && !moved && visit.state !== "open" && <Closed v={visit} />}
      {visit && !moved && visit.state === "open" && (
        <>
          <h1 className="font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl mb-8">Change your visit time</h1>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <CurrentVisit v={visit} />
              <p className="text-sm text-[#6B7280] mt-5 leading-relaxed">Pick any free slot and we&rsquo;ll release your current one. You&rsquo;ll get a confirmation email straight away — nothing to pay, as before.</p>
              <p className="text-xs text-[#6B7280] mt-3 inline-flex items-center gap-1.5"><Phone size={13} /> None of these work? Call <b>{visit.phone}</b>.</p>
            </div>
            <div>
              <SlotsFromLink slots={visit.slots} slot={slot} setSlot={setSlot} />
              <button data-testid="svm-confirm" disabled={busy || !slot.time} onClick={confirm} className="de-btn-primary mt-6 disabled:opacity-60">
                {busy ? "Moving…" : slot.time ? `Move my visit to ${new Date(slot.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} · ${slot.time}` : "Choose a new slot"} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function CurrentVisit({ v }) {
  return (
    <div className="bg-[#12263A] text-white p-6" data-testid="svm-current">
      <div className="text-xs uppercase tracking-widest text-[#C79A3B]">Currently booked</div>
      <div className="text-2xl font-black mt-1 inline-flex items-center gap-2"><Clock size={20} className="text-[#C79A3B]" /> {longDate(v.date)}{v.time ? ` · ${v.time}` : ""}</div>
      <div className="text-sm text-white/80 mt-2 inline-flex items-center gap-1.5"><MapPin size={14} /> {v.address || "address on file"}</div>
      <div className="text-xs text-white/60 mt-1">Reference {v.reference} · about 1 hour · nothing to pay at the visit</div>
    </div>
  );
}

// The manage endpoint already returns live availability, so render it directly (same look as the booking step).
function SlotsFromLink({ slots, slot, setSlot }) {
  if (!slots) return <SiteVisitSlots date={slot.date} time={slot.time} onChange={(date, time) => setSlot({ date, time })} />;
  const selected = slots.find(d => d.date === slot.date);
  const dayLabel = (iso) => new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
  return (
    <div data-testid="svm-slots">
      <div className="label-eyebrow mb-2">1 · Pick a new day</div>
      <div className="border border-[#E5E7EB] p-3 sm:p-4 max-h-[240px] overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-2 bg-white">
        {slots.length === 0 && <div className="col-span-full text-sm text-[#6B7280]">No slots available right now — please call us.</div>}
        {slots.map(d => (
          <button key={d.date} type="button" data-testid={`svm-day-${d.date}`} onClick={() => setSlot({ date: d.date, time: "" })}
            className={`px-1 py-2.5 text-xs border whitespace-nowrap ${slot.date === d.date ? "bg-[#C79A3B] text-white border-[#C79A3B]" : "border-[#E5E7EB] hover:border-[#12263A]"}`}>{dayLabel(d.date)}</button>
        ))}
      </div>
      <div className="label-eyebrow mb-2 mt-5">2 · Pick a time</div>
      {!selected ? <div className="text-sm text-[#6B7280] border border-dashed border-[#E5E7EB] p-4 bg-white">Choose a day above to see the times we can visit.</div> : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="svm-times">
          {selected.slots.map(t => (
            <button key={t} type="button" data-testid={`svm-time-${t.replace(":", "")}`} onClick={() => setSlot({ date: slot.date, time: t })}
              className={`py-3 text-sm font-bold border ${slot.time === t ? "bg-[#12263A] text-white border-[#12263A]" : "border-[#E5E7EB] bg-white hover:border-[#12263A]"}`}>{t}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function Moved({ v }) {
  return (
    <div className="max-w-2xl" data-testid="svm-moved">
      <h1 className="font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl mb-6">Your visit has moved</h1>
      <div className="border border-[#1F6B3B] bg-[#D8EED8] p-6 flex gap-4">
        <CheckCircle2 size={28} className="text-[#1F6B3B] shrink-0" />
        <div>
          <div className="text-xl font-black text-[#12263A]" data-testid="svm-moved-when">{v.date_label} at {v.time}</div>
          <div className="text-sm text-[#12263A]/80 mt-1">Reference <b>{v.reference}</b> · about 1 hour · nothing to pay at the visit</div>
          <p className="text-sm text-[#12263A]/80 mt-3">{v.email_sent ? "We've emailed you a confirmation of the new time." : "Your new time is saved — the office has been told."} Your deposit is only due after the visit, and only if you decide to go ahead.</p>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <Link to="/portal" className="de-btn-primary"><CalendarCheck size={16} /> Open my client portal</Link>
        <Link to="/" className="de-btn-secondary">Back to home</Link>
      </div>
    </div>
  );
}

function Closed({ v }) {
  const done = v.state === "done";
  return (
    <div className="max-w-2xl" data-testid="svm-closed">
      <h1 className="font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl mb-6">{done ? "This visit has already happened" : "This visit was cancelled"}</h1>
      <p className="text-[#6B7280]">{done ? "Thanks for having us — if you'd like to arrange another visit or talk through your quote, " : "To rebook, "}call us on <b>{v.phone}</b> or sign in to your client portal.</p>
      <div className="mt-6 flex gap-3"><Link to="/portal" className="de-btn-primary">Client portal</Link><Link to="/contact" className="de-btn-secondary">Contact us</Link></div>
    </div>
  );
}

function Invalid({ message }) {
  return (
    <div className="max-w-2xl border border-[#E9D9B8] bg-[#FBF7EF] p-6 flex gap-3" data-testid="svm-invalid">
      <AlertTriangle size={22} className="text-[#C79A3B] shrink-0" />
      <div><div className="font-black text-[#12263A]">We couldn&rsquo;t find that visit</div><p className="text-sm text-[#6B7280] mt-1">{message}</p></div>
    </div>
  );
}
